import AuthWall from '../../../components/auth-wall';
import AccessWall from '../../../components/access-wall';
import {observer} from 'mobx-react';
import Tabs from '../components/tabs/tabs';
import './networkAnalysisPage.scss';
import './rosterComparisonPage.scss';
import {MouseEvent, useCallback, useContext, useEffect, useMemo} from 'react';
import {NetworkContext} from '../lib/context';
import type {RosterRef} from '../lib/stores/roster-comparison-store';
import CompareToggle from '../components/compare-toggle';
import RosterComparisonTable from '../components/roster-comparison-table';
import ComparisonSummaryPanel from '../components/comparison-summary-panel';
import RosterComparisonGeographyFilter from '../components/no-filters/roster-comparison-geography-filter';
import PrimaryRosterSelect from '../components/primary-roster-select';
import {useNavigate, useParams} from 'react-router-dom';
import {SkeletonPlaceholder} from '@carbon/react';
import {SiteFeature} from '../../../lib/check-access';

const RESULTS_ID = 'roster-comparison-results';

// Moves focus to the results explicitly: a plain #hash link scrolls but does not always move focus
const skipToResults = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const results = document.getElementById(RESULTS_ID);
    results?.focus();
    results?.scrollIntoView();
};

// Summary | table | filters, as one grid row, so all three columns are as tall as the tallest and
// the summary and Filters both reach the bottom. The summary sets its own width, so collapsing it
// hands the space straight to the table. The DOM order is filters, summary, results, which is the
// order they gate each other in; the grid places them back left to right on wide screens
const NetworkAdequacyView = observer(() => (
    <div className="roster-comparison-layout bg-background-gray-100" data-testid="network-adequacy-view">
        {/* The filters come first in the DOM, so this is the keyboard shortcut past them and the summary */}
        <a className='roster-comparison-skip-link' href={`#${RESULTS_ID}`} onClick={skipToResults}>
            Skip to results
        </a>
        <aside className='roster-comparison-layout__filters network-analysis-page' aria-label='Roster comparison filters'>
            <RosterComparisonGeographyFilter
                showRosterSelection={true}
            />
        </aside>
        <ComparisonSummaryPanel />
        <section
            id={RESULTS_ID}
            tabIndex={-1}
            className='roster-comparison-layout__content network-analysis-page'
            aria-label='Roster comparison results'
        >
            <RosterComparisonTable />
        </section>
    </div>
));

function RosterComparisonPage() {
    const networkOptimizationStore = useContext(NetworkContext);
    const comparisonStore = networkOptimizationStore.rosterComparisonStore;
    const {rosterId, revisionId} = useParams();
    const navigate = useNavigate();

    const tabs = useMemo(() => [
        {
            label: 'Network Adequacy',
            content: <NetworkAdequacyView />
        }
    ], []);

    // Cleared when the page closes or the route moves to another roster (the page stays mounted),
    // so its rosters aren't compared against the new one. A move to another revision of the same
    // roster keeps the comparison. Effect cleanups run before effects, so this clears before the
    // new primary is set below
    useEffect(() => () => comparisonStore.reset(), [comparisonStore, rosterId]);

    // The route's roster and revision is the primary: the roster picked in My Files, roster 1,
    // when the page opens, then whichever revision the Primary Roster select moves the route to
    useEffect(() => {
        if (rosterId && revisionId) {
            comparisonStore.setPrimary(rosterId, revisionId);
        }
    }, [comparisonStore, rosterId, revisionId]);

    // Replaces the history entry, as the Network Analysis revision selector does, so Back leaves
    // the page rather than stepping through earlier primaries
    const showPrimaryInRoute = useCallback(
        (primary: RosterRef) => {
            navigate(`/network-optimization/${primary.rosterId}/${primary.revisionId}/compare`, {replace: true});
        },
        [navigate],
    );

    return (
        <AuthWall>
            <AccessWall
                siteFeature={SiteFeature.NetworkOptimizationRosterComparison}
                accessDeniedMessage='You do not have permission to access Roster Comparison. This feature requires special access. Please contact your administrator or customer support for assistance.'
                returnPath='/network-optimization'
            >
                <main className="network-analysis-page roster-comparison-page bg-white">
                    <div className="roster-comparison-heading flex flex-col justify-center rounded bg-white">
                        <div className="flex flex-col justify-center">
                            <h1 className="text-3xl text-[#222B36] font-normal m-0">Roster Comparison Tool</h1>
                        </div>
                        {networkOptimizationStore.rosterFileName ?
                            <div className="m-0 mt-3 mb-4 flex flex-wrap gap-x-5 gap-y-2 items-center text-[14px]">
                                {/* Offers the revisions of the roster picked in My Files */}
                                <PrimaryRosterSelect rosterId={rosterId} onPrimaryChange={showPrimaryInRoute} />
                                <CompareToggle />
                            </div> :
                            <SkeletonPlaceholder className='w-[320px] h-8 mt-3 mb-4' />
                        }
                    </div>
                    <Tabs tabs={tabs} variant="default" />
                </main>
            </AccessWall>
        </AuthWall>
    );
}

export default observer(RosterComparisonPage);
